import { Component } from '@theme/component';

/**
 * <scroll-reveal-component> : fait apparaître progressivement ses enfants marqués de l'attribut
 * `data-scroll-reveal` lorsqu'ils entrent dans la fenêtre visible, via IntersectionObserver natif
 * (aucune dépendance externe, aucun jQuery).
 *
 * Comportement :
 * - Respecte `prefers-reduced-motion` : si l'utilisateur préfère moins d'animations, tout le
 *   contenu est révélé immédiatement et aucun observateur n'est créé.
 * - Compatible avec le chargement progressif (infinite scroll) : un MutationObserver surveille
 *   l'ajout de nouveaux éléments dans le sous-arbre (ex. cartes produit ajoutées automatiquement
 *   en bas de la page collection) et les observe dès leur insertion, sans configuration
 *   supplémentaire.
 * - Dégradation silencieuse : le CSS associé (voir snippets/scroll-reveal-styles.liquid) garde le
 *   contenu visible par défaut tant que ce composant n'est pas défini/chargé (sélecteur
 *   `:defined`) — un échec de chargement du script ne peut donc jamais rendre du contenu invisible.
 * - Léger décalage en cascade entre éléments proches pour un rendu premium, plafonné pour éviter
 *   un délai cumulé excessif sur les longues grilles.
 */
class ScrollRevealComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #intersectionObserver;

  /** @type {MutationObserver | undefined} */
  #mutationObserver;

  connectedCallback() {
    super.connectedCallback();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#revealAll();
      return;
    }

    this.#intersectionObserver = new IntersectionObserver(this.#handleIntersect, {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    });

    this.#observeNewItems();

    // Couvre l'ajout dynamique de cartes produit (infinite scroll) : toute nouvelle balise
    // portant `data-scroll-reveal` insérée dans ce sous-arbre est automatiquement observée.
    this.#mutationObserver = new MutationObserver(this.#observeNewItems);
    this.#mutationObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  /**
   * Recherche les éléments `[data-scroll-reveal]` pas encore pris en charge (au chargement initial
   * ou ajoutés dynamiquement) et les ajoute à l'IntersectionObserver, avec un délai en cascade.
   */
  #observeNewItems = () => {
    const items = this.querySelectorAll('[data-scroll-reveal]:not([data-scroll-reveal-ready])');

    items.forEach((item, index) => {
      item.setAttribute('data-scroll-reveal-ready', 'true');
      // Cascade plafonnée à 8 éléments (480ms max) : au-delà, tous démarrent avec le même délai
      // pour éviter une attente trop longue en bas d'une grille de nombreux produits.
      item.style.setProperty('--scroll-reveal-delay', `${(index % 8) * 60}ms`);
      this.#intersectionObserver?.observe(item);
    });
  };

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.setAttribute('data-scroll-revealed', 'true');
      this.#intersectionObserver?.unobserve(entry.target);
    }
  };

  #revealAll() {
    this.querySelectorAll('[data-scroll-reveal]').forEach((item) => {
      item.setAttribute('data-scroll-revealed', 'true');
    });
  }
}

if (!customElements.get('scroll-reveal-component')) {
  customElements.define('scroll-reveal-component', ScrollRevealComponent);
}
